"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    Curseur,
    Ecran,
    Liste,
    Montant,
    Question,
    Recommencer,
    Resultat,
    Segments,
    euros,
    pourcent,
    remplir,
    useParcours,
} from "./Journey";
import {
    ECART_NEGLIGEABLE,
    bascule,
    comparerRegimes,
} from "@/lib/tools/businessStructure";
import { expliquerRegime } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Micro, réel ou société : lequel me laisse le plus ?
 *
 * LA SUITE DU CARROUSEL, ET SA CORRECTION. Le carrousel dit « si tes charges
 * dépassent ton abattement, la micro te coûte de l'argent ». C'est la bonne
 * intuition, et c'est un raccourci : le réel passe devant AVANT ce point, parce
 * que ses cotisations portent sur le bénéfice quand celles de la micro portent
 * sur la totalité du chiffre d'affaires. En prestations de services, la bascule
 * tombe vers 30 % de dépenses, pas 50 %. L'outil existe pour montrer ce chiffre
 * plutôt que de laisser croire au raccourci.
 *
 * CE QU'IL REFUSE DE FAIRE, ET POURQUOI IL LE DIT
 *
 * 1. Désigner un gagnant à moins de mille euros d'écart. En dessous, la
 *    différence est plus petite que ce que coûte la comptabilité d'une société
 *    et que l'imprécision assumée du calcul. L'écran le dit au lieu d'inventer
 *    un vainqueur.
 *
 * 2. Chiffrer une société au centime. Le coût affiché pour la SASU est un
 *    plancher : la cotisation accidents du travail a un taux notifié à chaque
 *    entreprise. La formation professionnelle et l'apprentissage sont comptés
 *    depuis le 24/09/2026, et la contribution des indépendants depuis le 25.
 *    L'erreur va donc dans le sens prudent, celui qui ne pousse pas à créer
 *    une société.
 *
 * 3. Simuler l'arbitrage rémunération-dividendes. On suppose que le dirigeant
 *    se verse tout, ce qui est le cas d'un solopreneur qui vit de son activité.
 *    L'arbitrage change le résultat, et c'est là qu'un expert-comptable gagne
 *    son prix : l'écran l'écrit plutôt que de le deviner.
 *
 * TOUT RESTE DANS LE NAVIGATEUR. Aucun appel réseau, aucun stockage : les
 * chiffres d'affaires et les revenus du foyer que le lecteur saisit ne sortent
 * pas de sa machine, et l'écran d'entrée le promet.
 */

const PHASES = {
    activite: 0,
    chiffreAffaires: 1,
    charges: 1,
    foyer: 2,
    autresRevenus: 2,
    resultat: 3,
};

const L = {
    fr: {
        phases: ["Activité", "Chiffres", "Foyer"],
        retour: "Retour",
        activite: {
            titre: "Quelle est votre activité ?",
            aide: "Elle fixe l'abattement de la micro-entreprise, et le taux de vos cotisations.",
            choix: [
                {
                    cle: "services",
                    icone: "case-minimalistic",
                    titre: "Prestations de services",
                    detail: "Artisanat, bâtiment, coiffure, transport. Abattement de 50 %.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square",
                    titre: "Activité libérale",
                    detail: "Conseil, développement, design, rédaction. Abattement de 34 %.",
                },
                {
                    cle: "vente",
                    icone: "cart-large-minimalistic",
                    titre: "Vente de marchandises",
                    detail: "Achat-revente, hébergement. Abattement de 71 %.",
                },
            ],
        },
        chiffreAffaires: {
            titre: "Combien encaissez-vous dans l'année ?",
            aide: "Hors taxes, tout ce qui entre sur le compte de l'activité.",
            libelle: "Chiffre d'affaires annuel",
            suivant: "Continuer",
        },
        charges: {
            titre: "Et combien dépensez-vous pour l'exercer ?",
            aide: "Matériel, abonnements, publicité, sous-traitance, déplacements, local. Tout ce que vous payez pour travailler, et que la micro ne déduit pas.",
            libelle: "Dépenses professionnelles annuelles",
            part: "Soit {part} de votre chiffre d'affaires.",
            suivant: "Continuer",
        },
        foyer: {
            titre: "Qui compose votre foyer fiscal ?",
            aide: "L'impôt se calcule sur tout le foyer. Sans lui, la comparaison serait fausse.",
            situation: "Situation",
            seul: "Seul",
            couple: "En couple",
            enfants: "Enfants à charge",
            suivant: "Continuer",
        },
        autresRevenus: {
            titre: "D'autres revenus dans le foyer ?",
            aide: "Un salaire, une pension, l'activité de votre conjoint. Nous ne comptons que l'impôt en plus que votre activité vous fait payer.",
            libelle: "Autres revenus imposables du foyer",
            aucun: "Aucun autre revenu",
            suivant: "Comparer les régimes",
        },
        resultat: {
            titre: "Le comparatif",
            microGagne: "La micro-entreprise reste devant",
            microGagneDetail:
                "À {charges} de dépenses professionnelles, aucun autre régime ne vous laisserait davantage. Restez où vous êtes, et refaites ce calcul le jour où vos dépenses montent.",
            changer: "Quitter la micro vous rapporterait {gain} par an",
            changerDetail: "{regime} est le régime qui vous laisse le plus.",
            serre: "Trop serré pour bouger",
            serreDetail:
                "{regime} passe devant la micro-entreprise, mais de {gain} seulement sur l'année. En dessous de mille euros, la différence est plus petite que ce que coûte la comptabilité d'une société, et que l'imprécision de ce calcul.",
            exAequo:
                "Les deux premiers se tiennent à {ecart} près. Entre eux, laissez autre chose que l'argent décider : la protection sociale et la simplicité d'abord.",
            parAn: "par an",
            plancher: "au mieux",
            regimes: {
                micro: "Micro-entreprise",
                reel: "Entreprise individuelle au réel",
                eurl: "EURL à l'impôt sur les sociétés",
                sasu: "SASU à l'impôt sur les sociétés",
            },
            bascule: {
                titre: "Votre point de bascule",
                avant: "À {ca} de chiffre d'affaires, le réel passe devant la micro à partir de {bascule} de dépenses annuelles. Vous en êtes à {charges}.",
                jamais: "À ce niveau de chiffre d'affaires, la micro garde l'avantage quel que soit le montant des dépenses.",
                raccourci:
                    "Votre abattement forfaitaire vaut {abattement} : si vous attendez que vos charges le dépassent, le réel sera déjà passé devant.",
            },
            hypotheses: {
                titre: "Ce que ce calcul suppose",
                items: [
                    "En société, le dirigeant se verse tout ce qu'elle peut verser, et ne distribue pas de dividendes. L'arbitrage avec les dividendes change le résultat : l'outil « Rémunération ou dividendes » le calcule.",
                    "Le coût d'une SASU est un plancher : la cotisation accidents du travail, dont le taux est notifié à chaque entreprise, n'est pas comptée. Le versement mobilité ne concerne que les entreprises d'au moins onze salariés.",
                    "Nous ne chiffrons ni les honoraires d'un comptable, ni les taxes pour frais de chambre : aucune source officielle ne les publie sous une forme utilisable.",
                    "L'impôt retenu est celui que votre activité ajoute à votre foyer, pas l'impôt total du foyer.",
                ],
            },
            avertissement:
                "Ce comparatif est indicatif. Décidez d'un changement de régime avec un expert-comptable : il touche aussi votre protection sociale et votre retraite.",
            recommencer: "Refaire le calcul",
            cotisations: "Cotisations",
            impot: "Impôt sur le revenu",
            impotSocietes: "Impôt sur les sociétés",
            charges: "Dépenses professionnelles",
        },
    },
    en: {
        phases: ["Activity", "Figures", "Household"],
        retour: "Back",
        activite: {
            titre: "What do you do?",
            aide: "It sets the micro-entreprise allowance, and your contribution rates.",
            choix: [
                {
                    cle: "services",
                    icone: "case-minimalistic",
                    titre: "Services",
                    detail: "Trades, construction, transport. 50% allowance.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square",
                    titre: "Liberal profession",
                    detail: "Consulting, development, design, writing. 34% allowance.",
                },
                {
                    cle: "vente",
                    icone: "cart-large-minimalistic",
                    titre: "Selling goods",
                    detail: "Buying and reselling, accommodation. 71% allowance.",
                },
            ],
        },
        chiffreAffaires: {
            titre: "How much do you collect in a year?",
            aide: "Excluding VAT: everything that lands on the business account.",
            libelle: "Annual turnover",
            suivant: "Continue",
        },
        charges: {
            titre: "And how much do you spend to earn it?",
            aide: "Equipment, subscriptions, advertising, subcontracting, travel, premises. Everything you pay to work, and that a micro-entreprise does not deduct.",
            libelle: "Annual business expenses",
            part: "That is {part} of your turnover.",
            suivant: "Continue",
        },
        foyer: {
            titre: "Who is in your tax household?",
            aide: "Income tax is worked out on the whole household. Without it, the comparison would be wrong.",
            situation: "Situation",
            seul: "On my own",
            couple: "In a couple",
            enfants: "Dependent children",
            suivant: "Continue",
        },
        autresRevenus: {
            titre: "Any other household income?",
            aide: "A salary, a pension, your partner's work. We only count the extra tax your business makes you pay.",
            libelle: "Other taxable household income",
            aucun: "No other income",
            suivant: "Compare",
        },
        resultat: {
            titre: "The comparison",
            microGagne: "The micro-entreprise still wins",
            microGagneDetail:
                "At {charges} of business expenses, no other structure would leave you more. Stay where you are, and run this again the day your spending goes up.",
            changer: "Leaving the micro-entreprise would earn you {gain} a year",
            changerDetail: "{regime} is the structure that leaves you the most.",
            serre: "Too close to move",
            serreDetail:
                "{regime} overtakes the micro-entreprise, but by only {gain} over a year. Below a thousand euros, the gap is smaller than a company's accounting costs, and smaller than this calculation's own margin.",
            exAequo:
                "The top two are within {ecart} of each other. Between them, let something other than money decide: social cover and simplicity first.",
            parAn: "per year",
            plancher: "at best",
            regimes: {
                micro: "Micro-entreprise",
                reel: "Sole trader, actual expenses",
                eurl: "EURL, corporate tax",
                sasu: "SASU, corporate tax",
            },
            bascule: {
                titre: "Your tipping point",
                avant: "At {ca} of turnover, actual expenses overtake the micro-entreprise from {bascule} of annual spending. You are at {charges}.",
                jamais: "At this level of turnover, the micro-entreprise keeps the edge whatever you spend.",
                raccourci:
                    "Your flat-rate allowance is {abattement}: if you wait for your expenses to exceed it, actual expenses will already have taken the lead.",
            },
            hypotheses: {
                titre: "What this calculation assumes",
                items: [
                    "In a company, the director pays themselves everything the company can pay, and distributes no dividends. The salary-dividend trade-off changes the result: the « Salary or dividends » tool calculates it.",
                    "The cost of a SASU is a floor: the workplace accident contribution, whose rate is notified to each company, is not counted. The mobility levy only applies to companies with at least eleven employees.",
                    "We do not cost an accountant's fees or chamber levies: no official source publishes them in a usable form.",
                    "The tax counted is what your business adds to your household, not the household's total tax.",
                ],
            },
            avertissement:
                "This comparison is indicative. Decide on any change of structure with an accountant: it also affects your social cover and your pension.",
            recommencer: "Start again",
            cotisations: "Social contributions",
            impot: "Income tax",
            impotSocietes: "Corporate tax",
            charges: "Business expenses",
        },
    },
};

/** Le rang d'un régime, pour la médaille et la teinte de sa ligne. */
const MEDAILLES = ["bg-primary/10 border-primary/40", "", "", ""];

export default function ComparateurRegimes({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    const { valeurs: dossier } = useDossierOutil();
    const [activite, setActivite] = useState(dossier.activite ?? null);
    const [chiffreAffaires, setChiffreAffaires] = useState(dossier.chiffreAffaires ?? 60000);
    const [charges, setCharges] = useState(dossier.charges ?? 15000);
    const [enCouple, setEnCouple] = useState(dossier.enCouple ?? false);
    const [enfants, setEnfants] = useState(dossier.enfants ?? 0);
    const [autresRevenus, setAutresRevenus] = useState(dossier.autresRevenus ?? 0);

    const { etape, communs, rang, aller, retour, recommencer, reduit } = useParcours(
        "activite",
        () => {
            setActivite(null);
            setChiffreAffaires(60000);
            setCharges(15000);
            setEnCouple(false);
            setEnfants(0);
            setAutresRevenus(0);
        },
        PHASES,
        reprendre([
            { etape: "activite", connu: dossier.activite !== undefined },
            // Un chiffre hors des bornes du curseur (zéro pour un projet) se redemande.
            {
                etape: "chiffreAffaires",
                connu: dossier.chiffreAffaires >= 5000 && dossier.chiffreAffaires <= 250000,
            },
            { etape: "charges", connu: dossier.charges !== undefined },
            { etape: "foyer", connu: dossier.enCouple !== undefined && dossier.enfants !== undefined },
            { etape: "autresRevenus", connu: dossier.autresRevenus !== undefined },
            { etape: "resultat" },
        ]),
    );

    useSignalerDossier(
        { activite, chiffreAffaires, charges, enCouple, enfants, autresRevenus },
        etape === "resultat",
    );

    const parametres = useMemo(
        () => ({
            chiffreAffaires,
            charges: Math.min(charges, chiffreAffaires),
            activite: activite || "services",
            enCouple,
            enfants,
            autresRevenus,
        }),
        [chiffreAffaires, charges, activite, enCouple, enfants, autresRevenus],
    );

    const resultat = useMemo(
        () => (etape === "resultat" ? comparerRegimes(parametres) : null),
        [etape, parametres],
    );
    const point = useMemo(
        () => (etape === "resultat" ? bascule(parametres) : null),
        [etape, parametres],
    );

    const communsEcran = {
        ...communs,
        phases: t.phases,
        retour: t.retour,
        onRetour: retour,
    };
    const sommes = (v) => euros(v, lang);
    // Un seul calcul déplié à la fois, sous le régime cliqué.
    const { ouverte, basculer } = useExplications();
    const explication = (s) =>
        expliquerRegime(
            s,
            {
                chiffreAffaires: parametres.chiffreAffaires,
                charges: parametres.charges,
                activite: parametres.activite,
                artisan: false,
                foyer: { enCouple, enfants, parentIsole: false, autresRevenus },
            },
            lang,
        ).net;

    if (etape === "activite") {
        return (
            <Question
                key="activite"
                {...communsEcran}
                phase={0}
                rang={rang}
                titre={t.activite.titre}
                aide={t.activite.aide}
                choix={t.activite.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        setActivite(c.cle);
                        aller("chiffreAffaires");
                    },
                }))}
            />
        );
    }

    if (etape === "chiffreAffaires") {
        return (
            <Ecran
                key="chiffreAffaires"
                {...communsEcran}
                phase={1}
                rang={rang}
                titre={t.chiffreAffaires.titre}
                aide={t.chiffreAffaires.aide}
            >
                <Curseur
                    libelle={t.chiffreAffaires.libelle}
                    valeur={chiffreAffaires}
                    poser={setChiffreAffaires}
                    minimum={5000}
                    maximum={250000}
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("charges")}>{t.chiffreAffaires.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "charges") {
        const part = chiffreAffaires > 0 ? charges / chiffreAffaires : 0;
        return (
            <Ecran
                key="charges"
                {...communsEcran}
                phase={1}
                rang={rang}
                titre={t.charges.titre}
                aide={t.charges.aide}
            >
                <Curseur
                    libelle={t.charges.libelle}
                    aide={remplir(t.charges.part, { part: pourcent(part, lang, 0) })}
                    valeur={charges}
                    poser={setCharges}
                    minimum={0}
                    maximum={chiffreAffaires}
                    pas={500}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("foyer")}>{t.charges.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "foyer") {
        return (
            <Ecran
                key="foyer"
                {...communsEcran}
                phase={2}
                rang={rang}
                titre={t.foyer.titre}
                aide={t.foyer.aide}
            >
                <div className="space-y-5">
                    <div>
                        <div id="statuts-foyer-situation" className="text-sm text-gray-600 dark:text-lightBlue mb-2">
                            {t.foyer.situation}
                        </div>
                        <Segments
                            labelledBy="statuts-foyer-situation"
                            options={[
                                { cle: "seul", libelle: t.foyer.seul },
                                { cle: "couple", libelle: t.foyer.couple },
                            ]}
                            valeur={enCouple ? "couple" : "seul"}
                            poser={(v) => setEnCouple(v === "couple")}
                        />
                    </div>
                    <Curseur
                        libelle={t.foyer.enfants}
                        valeur={enfants}
                        poser={setEnfants}
                        minimum={0}
                        maximum={6}
                        pas={1}
                        formater={(v) => String(v)}
                    />
                </div>
                <Avancer onClick={() => aller("autresRevenus")}>{t.foyer.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "autresRevenus") {
        return (
            <Ecran
                key="autresRevenus"
                {...communsEcran}
                phase={2}
                rang={rang}
                titre={t.autresRevenus.titre}
                aide={t.autresRevenus.aide}
            >
                <Curseur
                    libelle={t.autresRevenus.libelle}
                    aide={autresRevenus === 0 ? t.autresRevenus.aucun : undefined}
                    valeur={autresRevenus}
                    poser={setAutresRevenus}
                    minimum={0}
                    maximum={120000}
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("resultat")}>{t.autresRevenus.suivant}</Avancer>
            </Ecran>
        );
    }

    const classes = resultat.classes;
    const nom = (regime) => r.regimes[regime];

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} {...communs}>
            {/* LE VERDICT RÉPOND À LA QUESTION POSÉE, qui est « dois-je quitter la
                micro ? », et non « quel est le meilleur des quatre ». Comparer les
                deux premiers du classement finirait par annoncer « trop serré »
                alors que la micro est loin derrière : l'EURL et le réel se tiennent
                presque toujours à l'abattement de 10 % près. */}
            <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">
                {resultat.microEnTete
                    ? r.microGagne
                    : resultat.changementUtile
                      ? remplir(r.changer, { gain: sommes(resultat.gainSiChangement) })
                      : r.serre}
            </div>
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2">
                {resultat.microEnTete
                    ? remplir(r.microGagneDetail, { charges: sommes(parametres.charges) })
                    : resultat.changementUtile
                      ? remplir(r.changerDetail, { regime: nom(resultat.meilleurRegime) })
                      : remplir(r.serreDetail, {
                            regime: nom(resultat.meilleurRegime),
                            gain: sommes(resultat.gainSiChangement),
                        })}
            </p>
            {!resultat.microEnTete && resultat.exAequo && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2">
                    {remplir(r.exAequo, { ecart: sommes(resultat.ecart) })}
                </p>
            )}

            {/* Le classement, le seul écran que le lecteur lira vraiment. */}
            <IndiceExplication lang={lang} />
            <div className="mt-3 space-y-2">
                {classes.map((s, i) => (
                    <div
                        key={s.regime}
                        className={`rounded-xl border px-4 py-3 ${
                            MEDAILLES[i] || "border-gray-200/70 dark:border-white/10"
                        }`}
                    >
                    <div className="flex items-baseline justify-between gap-4">
                        <div className="min-w-0">
                            <div className="font-semibold dark:text-white leading-snug">
                                {nom(s.regime)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-0.5">
                                {r.cotisations} {sommes(Math.round(s.cotisations))}
                                {" · "}
                                {r.impot} {sommes(Math.round(s.impot))}
                                {s.impotSocietes > 0 &&
                                    ` · ${r.impotSocietes} ${sommes(Math.round(s.impotSocietes))}`}
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="font-bold tabular-nums dark:text-white">
                                <MontantExplicable
                                    explication={explication(s)}
                                    ouvert={ouverte === s.regime}
                                    onBasculer={() => basculer(s.regime)}
                                >
                                    <Montant
                                        valeur={Math.max(0, Math.round(s.net))}
                                        lang={lang}
                                        reduit={reduit}
                                    />
                                </MontantExplicable>
                            </div>
                            <div className="text-[11px] text-gray-500 dark:text-lightBlue/70">
                                {s.plancher ? r.plancher : r.parAn}
                            </div>
                        </div>
                    </div>
                    {ouverte === s.regime && <PanneauExplication explication={explication(s)} lang={lang} />}
                    </div>
                ))}
            </div>

            {/* Le chiffre que le lecteur est venu chercher. */}
            <div className="mt-6 rounded-xl bg-gray-50 dark:bg-white/[0.04] px-4 py-4">
                <div className="flex items-center gap-2 font-semibold dark:text-white">
                    <SolarIcon name="chart-2" className="w-5 h-5 text-primary" />
                    {r.bascule.titre}
                </div>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                    {point === null
                        ? r.bascule.jamais
                        : remplir(r.bascule.avant, {
                              ca: sommes(chiffreAffaires),
                              bascule: sommes(point),
                              charges: sommes(parametres.charges),
                          })}
                </p>
                {point !== null && point < resultat.abattementForfaitaire && (
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {remplir(r.bascule.raccourci, {
                            abattement: sommes(Math.round(resultat.abattementForfaitaire)),
                        })}
                    </p>
                )}
            </div>

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.hypotheses.titre}</div>
                <Liste items={r.hypotheses.items} />
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0 leading-relaxed">
                {r.avertissement}
            </p>

            <Recommencer onClick={recommencer} libelle={r.recommencer} />
        </Resultat>
    );
}

/** Exporté pour le contrôle : l'écart sous lequel l'écran refuse de trancher. */
export { ECART_NEGLIGEABLE };
